import { APIStatus } from "@/api/APIStatus.type";
import LoadingPlayersList from "@/components/organisms/PlayersList/LoadingPlayersList";
import { MyEvolutionsEmptyState } from "@/components/organisms/PlayersList/MyEvolutionsEmptyState";
import { PlayerList } from "@/components/organisms/PlayersList/PlayersList";
import { PlayersListEmptyState } from "@/components/organisms/PlayersList/PlayersListEmptyState";
import { Pagination } from "@/design-system/Pagination/Pagination";
import { trackFilter } from "@/events/evolutionsMixpanel";
import { cn } from "@/lib/utils";
import {
  RangeFilterType,
  SelectorFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { UpdateFilterType } from "@/pages/Players/FiltersPanel/filterTypes";
import {
  BasePath,
  MetaRatingsPlayer,
  PlayerRole,
  ownedOrEvolvedSelector,
} from "@/redux/players/playersSlice";
import type { PlayersSortByType } from "@repo/shared/api";
import { ReactNode } from "react";
import { useSelector } from "react-redux";

import { ToolbarAction } from "../EvolutionsPage/EvolutionsPage";
import { EvolutionsStickySidebar } from "./EvolutionsStickySidebar";

interface Props {
  elementOpened: ToolbarAction;
  playersStatus: APIStatus;
  players: MetaRatingsPlayer[];
  isTouchDevice: boolean;
  sortBy: PlayersSortByType;
  selectedPlayerRoles: PlayerRole[];
  playerFilters: (SelectorFilterType | RangeFilterType)[];
  scrollPastTopBar: boolean;
  handleToolbarAction: (action: ToolbarAction) => void;
  hasFilters: boolean;
  playerRoles: PlayerRole[];
  handleSelectPlayerRoles?: (playerRoles: PlayerRole[]) => void;
  handleSelectPlayerRole?: (playerRole: PlayerRole) => void;
  multiSelectPlayerRoles?: boolean;
  refPlayerRole: React.RefObject<HTMLDivElement>;
  refFilters: React.RefObject<HTMLDivElement>;
  handleMyClubClicked: (
    f: UpdateFilterType,
    type: "own-evo-player" | "own-player" | "active-squad",
  ) => void;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  setSortBy?: (sortBy: PlayersSortByType) => void;
  searchTermErrorMessage?: string;
  onFiltersUpdated: () => void;
  /** Forwarded to PlayerList — see its props. */
  basePath?: BasePath | ((player: MetaRatingsPlayer) => BasePath);
  onSelectPlayer?: (player: MetaRatingsPlayer) => void;
  showEvoPopularity?: boolean;
  builderContext?: "evolution-builder" | "evolution-builder-with-player";
  /** List rows or the front page's card grid. */
  view?: "list" | "grid";
  /** Forwarded to PlayerList: drawn between the second and third rows. */
  afterSecondRow?: ReactNode;
}

export const EvolutionsPlayersSection = ({
  elementOpened,
  playersStatus,
  players,
  isTouchDevice,
  sortBy,
  selectedPlayerRoles,
  playerFilters,
  scrollPastTopBar,
  handleToolbarAction,
  playerRoles,
  handleSelectPlayerRoles = () => {},
  handleSelectPlayerRole,
  multiSelectPlayerRoles = false,
  refPlayerRole,
  refFilters,
  handleMyClubClicked,
  page,
  totalPages,
  setPage,
  setSortBy,
  searchTermErrorMessage,
  onFiltersUpdated,
  basePath,
  onSelectPlayer,
  showEvoPopularity,
  builderContext,
  view = "list",
  afterSecondRow,
}: Props) => {
  const ownedOrEvolved = useSelector(ownedOrEvolvedSelector);

  return (
    <div
      className={cn(
        "flex flex-col gap-y-1 desktop:gap-y-4 mx-auto items-center w-full max-w-content px-3 large:px-16 touch-device:px-4 min-h-screen",
      )}
    >
      <div className="w-full flex flex-row gap-x-1">
        <div
          className={cn(
            "w-full flex flex-col gap-y-2 mt-3",
            (elementOpened === "playerRole" || elementOpened === "filters") &&
              "",
          )}
        >
          {(playersStatus === "pending" || playersStatus === "idle") &&
          (players.length === 0 || isTouchDevice) ? (
            <LoadingPlayersList />
          ) : players.length === 0 || searchTermErrorMessage ? (
            <PlayersListEmptyState
              searchTermErrorMessage={searchTermErrorMessage}
              category={ownedOrEvolved?.category}
              clubContext="evolution-players"
              section="Evolutions"
              evolutionsEmptyState={
                <MyEvolutionsEmptyState section="Evolutions" />
              }
            />
          ) : (
            <PlayerList
              players={players}
              show3Chem={
                sortBy === "meta-rating-3-chem" || sortBy === "upgrade-3-chem"
              }
              playerRoleId={
                selectedPlayerRoles.length === 1
                  ? selectedPlayerRoles[0].id
                  : undefined
              }
              basePath={basePath}
              onSelect={onSelectPlayer}
              showEvoPopularity={showEvoPopularity}
              view={view}
              afterSecondRow={afterSecondRow}
            />
          )}
        </div>
        {(elementOpened === "playerRole" ||
          elementOpened === "filters" ||
          elementOpened === "includeExclude") && (
          <EvolutionsStickySidebar
            onFiltersUpdated={onFiltersUpdated}
            elementOpened={elementOpened}
            handleToolbarAction={handleToolbarAction}
            isTouchDevice={isTouchDevice}
            playerRoles={playerRoles}
            playerRole={selectedPlayerRoles[0]}
            handleSelectPlayerRole={handleSelectPlayerRole}
            selectedPlayerRoles={selectedPlayerRoles}
            handleSelectPlayerRoles={handleSelectPlayerRoles}
            multiSelectPlayerRoles={multiSelectPlayerRoles}
            setSortBy={setSortBy}
            refPlayerRole={refPlayerRole}
            refFilters={refFilters}
            playerFilters={playerFilters}
            builderContext={builderContext}
          />
        )}
      </div>
      {players.length > 0 && !searchTermErrorMessage && (
        <Pagination
          className="mt-6 mb-10 w-full items-end"
          currentPage={page}
          totalPages={totalPages}
          onChange={(p) => {
            setPage(p);
            window.scrollTo(0, 0);
          }}
        />
      )}
    </div>
  );
};
